import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import type { ProspectTouchType } from '../../shared/prospect-engagement';

export type ProspectProfile = {
  id: string;
  playerId: string | null;
  linkedPlayerId: string | null;
  nhlId: number | null;
  headshotUrl: string | null;
  heroImageUrl: string | null;
  mediaCheckedAt: number | null;
  firstName: string;
  lastName: string;
  displayName: string;
  birthYear: number | null;
  position: string | null;
  shoots: string | null;
  team: string | null;
  league: string | null;
  rights: string | null;
  boardSection: string | null;
  boardRank: number | null;
  priorityGroup: number | null;
  status: 'active' | 'archived';
  notes: string | null;
  sourceSheet: string | null;
  tracker?: TouchTracker;
  currentLeague: string | null;
  contractExpireYear: number | null;
  updatedAt: number;
};

export type ProspectTouch = {
  id: string;
  prospectId: string | null;
  playerId: string | null;
  displayName: string | null;
  season: string;
  bucket: string;
  touchType: string;
  rawValue: string | null;
  note: string | null;
  occurredAt: string | null;
};

export type ProspectTeamView = {
  id: string;
  category: string;
  participants: string;
  teamName: string | null;
  nextViewing: string | null;
  viewingsTotal: string | null;
  notes: string | null;
};

export type ProspectInterest = {
  id: string;
  prospectId: string | null;
  playerId: string | null;
  displayName: string | null;
  sheet: string;
  context: string | null;
  organization: string;
  teamNeed: string | null;
  interest: string;
  notes: string | null;
};

export type ProspectScoutFeedback = {
  id: string;
  prospectId: string | null;
  playerId: string | null;
  displayName: string | null;
  sheet: string;
  scoutTeam: string;
  feedback: string;
};

export type ProspectVideo = {
  id: string;
  prospectId: string | null;
  playerId: string | null;
  displayName: string | null;
  season: string;
  expert: string | null;
  total: number | null;
  monthlyJson: string | null;
  notes: string | null;
};

export type ProspectsResponse = {
  profiles: ProspectProfile[];
  touches: ProspectTouch[];
  teamViews: ProspectTeamView[];
  interest: ProspectInterest[];
  scoutFeedback: ProspectScoutFeedback[];
  video: ProspectVideo[];
  engagement: {
    startsOn: string;
    dueOn: string;
    tierGoals: Record<'0' | '1' | '2' | '3', number>;
    touchTypes: Array<{ key: ProspectTouchType; label: string; points: number }>;
  };
};

export type ProspectTouchLogInput = {
  prospectId: string;
  entries: Array<{ touchType: ProspectTouchType; occurredAt: string }>;
  note?: string | null;
};

export type ProspectTouchBatchLogInput = {
  // One id per click, so a double submit saves once.
  requestId?: string;
  rows: ProspectTouchLogInput[];
};

export type ProspectBatchRow = {
  row: number;
  action: string;
  name: string;
  status: 'ready' | 'warning' | 'error' | 'applied';
  message: string;
  prospectId: string | null;
  playerId: string | null;
  patch: Record<string, unknown>;
};

export type ProspectBatchResponse = {
  preview: boolean;
  applied: number;
  batchId: string | null;
  rows: ProspectBatchRow[];
};

export type ProspectBatchHistoryItem = {
  id: string;
  batchId: string;
  action: string;
  diff: {
    rows?: number;
    applied?: number;
    rejected?: number;
    source?: string;
    results?: Array<{
      row: number;
      action: string;
      name: string;
      status: string;
      message: string;
    }>;
  } | null;
  occurredAt: number;
};

export type TouchTracker = 'prospect' | 'client';

// A touch the server skipped because that player already has it for that day.
export type SkippedTouch = { prospectId: string; displayName: string; touchType: string; occurredAt: string };

// Prospects and pro clients share one tracker; `tracker` picks the list.
export function useProspects(tracker: TouchTracker = 'prospect') {
  return useQuery<ProspectsResponse>({
    queryKey: ['prospects', tracker],
    queryFn: () => apiGet<ProspectsResponse>(tracker === 'client' ? '/api/prospects?tracker=client' : '/api/prospects'),
    staleTime: 10_000,
  });
}

export function usePatchProspectProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ProspectProfile> }) =>
      apiPatch<{ profile: ProspectProfile }>(`/api/prospects/profiles/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prospects'] }),
  });
}

export function usePatchProspectTable(table: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiPatch<{ ok: true }>(`/api/prospects/${table}/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prospects'] }),
  });
}

export function useLogProspectTouches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prospectId, entries, note }: ProspectTouchLogInput) =>
      apiPost<{ ok: true; logged: number; pointsAdded: number }>(
        `/api/prospects/profiles/${prospectId}/touches`,
        {
          entries,
          note: note || null,
        }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['prospects'] });
      void qc.invalidateQueries({ queryKey: ['player'] });
    },
  });
}

export function useLogProspectTouchBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProspectTouchBatchLogInput) =>
      apiPost<{
        ok: true;
        batchId: string;
        prospects: number;
        logged: number;
        pointsAdded: number;
        skipped?: SkippedTouch[];
      }>('/api/prospects/touches/batch', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['prospects'] });
      void qc.invalidateQueries({ queryKey: ['player'] });
    },
  });
}

export function useProspectBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, preview }: { csv: string; preview: boolean }) =>
      apiPost<ProspectBatchResponse>('/api/prospects/batch', { csv, preview }),
    onSuccess: (result) => {
      if (!result.preview) {
        void qc.invalidateQueries({ queryKey: ['prospects'] });
        void qc.invalidateQueries({ queryKey: ['prospects', 'batch-history'] });
        void qc.invalidateQueries({ queryKey: ['players'] });
      }
    },
  });
}

export function useProspectBatchHistory() {
  return useQuery<{ items: ProspectBatchHistoryItem[] }>({
    queryKey: ['prospects', 'batch-history'],
    queryFn: () => apiGet<{ items: ProspectBatchHistoryItem[] }>('/api/prospects/batch-history'),
    staleTime: 10_000,
  });
}
